import { useCallback, useMemo, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Coffee, Plus } from "lucide-react";
import { List } from "../../../../../../../../../components/shared/Form";
import Button from "../../../../../../../../../components/shared/Button";
import Dialog from "../../../../../../../../../components/shared/Dialog";
import Break from "./Break";
import AutomaticBreaks from "./AutomaticBreaks";
import { useScheduleContext } from "../../../../../../hooks/context/useScheduleContext";
import { useVisible } from "../../../../../../../../../utils/hooks/useVisible";
import { cn } from "../../../../../../../../../lib/utils";
import type { Resource } from "../../../../../../types/Resource";

function Breaks() {
    const { t } = useTranslation();
    const { formState, getValues, control, trigger } = useFormContext();
    const { selectedCellOrEvent } = useScheduleContext();
    const { open, visible, close } = useVisible();
    const [defaultBreaks, setDefaultBreaks] = useState<any[]>([]);

    const hasBreakErrors = !!formState.errors?.breaks;

    const openDialog = useCallback(() => {
        setDefaultBreaks(getValues("breaks") ?? []);
        open();
    }, [getValues, open]);

    const { canAddBreaks, hasAutomaticBreaks, breakPayCode } = useMemo(() => {
        const calcGroupBreaks = (selectedCellOrEvent?.user as Resource)?.calculationGroup?.breaks;
        const manualBreak = calcGroupBreaks?.manual;
        return {
            canAddBreaks: manualBreak?.status,
            hasAutomaticBreaks: !!calcGroupBreaks?.automatic?.status,
            // isMandatory: !!manualBreak?.mandatory,
            // mandatoryDuration: manualBreak?.duration,
            breakPayCode: manualBreak?.payCode
        };
    }, [selectedCellOrEvent?.user]);

    const breaks = useWatch({ control, name: "breaks" });

    const title = useMemo(() => {
        return (canAddBreaks || selectedCellOrEvent?.isUnassignedShift)
            ? t("add-breaks-to-shift-details")
            : t("breaks")
    }, [canAddBreaks, selectedCellOrEvent?.isUnassignedShift, t]);

    if (!canAddBreaks && !selectedCellOrEvent?.isUnassignedShift && !hasAutomaticBreaks && !breaks?.length) {
        return null;
    }

    return (
        <>
            <Button
                type="button"
                variant="ghost"
                className={cn("tw-mb-6", hasBreakErrors ? "tw-text-rose-500" : "tw-text-primary")}
                onClick={openDialog}
            >
                <Coffee />
                {(canAddBreaks || selectedCellOrEvent?.isUnassignedShift) ? t("add-break") : t("breaks")}
            </Button>

            <Dialog title={title} open={visible} close={close} size="2xl">
                {(canAddBreaks || selectedCellOrEvent?.isUnassignedShift) ? (
                    <div>
                        <List name="breaks">
                            {(fields, { add, remove, replace }) => {
                                const length = fields?.length || 0;

                                const cancel = () => {
                                    replace(defaultBreaks);
                                    close();
                                };

                                const confirm = async () => {
                                    const isValid = await trigger("breaks");
                                    if (isValid) {
                                        close();
                                    }
                                };

                                return (
                                    <>
                                        {length === 0 && (
                                            <div className="tw-flex tw-flex-col tw-items-center tw-gap-2 tw-py-3 tw-mb-2 tw-border-2 tw-rounded-xl tw-border-dashed">
                                                <img src="/assets/tea-cup.png" alt="" width={50} draggable="false" />
                                                <p className="tw-text-sm tw-font-medium tw-text-neutral-700">{t("breaks")}</p>
                                                <p className="tw-text-xs tw-text-neutral-400 tw-text-center">{t("add-breaks-to-shift-details")}</p>
                                            </div>
                                        )}

                                        {length > 0 && (
                                            <div className="tw-rounded-lg tw-border tw-border-neutral-100 tw-bg-neutral-50 tw-px-3 tw-mb-1 tw-divide-y tw-divide-neutral-100">
                                                {fields.map((field, index) => (
                                                    <Break
                                                        key={field.id}
                                                        name={`breaks.${index}`}
                                                        index={index}
                                                        remove={() => remove(index)}
                                                    />
                                                ))}
                                            </div>
                                        )}

                                        <div className={cn(length > 0 ? "tw-flex tw-justify-end" : "tw-w-full")}>
                                            <Button
                                                type="button"
                                                size="sm"
                                                className={cn(
                                                    length === 0
                                                        ? "tw-text-primary hover:tw-text-primary hover:tw-bg-primary/5 tw-border-primary tw-w-full"
                                                        : "tw-flex tw-items-center tw-gap-1 tw-text-xs tw-w-fit tw-text-primary"
                                                )}
                                                variant={length === 0 ? "outline" : "ghost"}
                                                onClick={() => add({ type: "break", start: undefined, end: undefined, payCode: breakPayCode })}
                                            >
                                                <Plus size={length === 0 ? 13 : 11} /> {t("add-break")}
                                            </Button>
                                        </div>

                                        <div className="tw-flex tw-items-center tw-gap-2 tw-justify-end tw-mt-4 tw-w-full">
                                            <Button variant="ghost" type="button" onClick={cancel}>
                                                {t("cancel")}
                                            </Button>
                                            <Button
                                                // variant="dark"
                                                type="button"
                                                onClick={confirm}
                                            >
                                                {t("confirm")}
                                            </Button>
                                        </div>
                                    </>
                                );
                            }}
                        </List>
                    </div>
                ) : <AutomaticBreaks breaks={breaks ?? []} />}

                {(!canAddBreaks && !selectedCellOrEvent?.isUnassignedShift) && (
                    <div className="tw-flex tw-justify-end">
                        <Button variant="ghost" type="button" onClick={close}>
                            {t("close")}
                        </Button>
                    </div>
                )}
            </Dialog>
        </>
    );
}

export default Breaks;
